/**
 * Sprint UX-BR — Sistema de internacionalização (i18n).
 *
 * USO BÁSICO:
 *   import { t } from '@/i18n';
 *   t('common.apply')             → "Aplicar"
 *   t('schedule.showingOf', {n:5, total:20})  → "Mostrando 5 de 20"
 *
 * ESTRATÉGIA:
 *   - Locale padrão: 'pt-BR' (Brasil)
 *   - Fallback: retorna a CHAVE se não encontra (facilita detectar gaps)
 *   - Locale futuro: 'en-US' (esqueleto preservado em enUS.js)
 *   - Trocar locale: setLocale('en-US') — não persiste; main.jsx pode
 *     ler de localStorage ou da URL no boot
 *
 * TERMOS TÉCNICOS PRESERVADOS (NÃO traduzir):
 *   device_id, customer_id, hostname, SSID, OTA, Wi-Fi, IP, Gateway,
 *   DNS, DHCP, relay_reboot, watchdog (como termo técnico), firmware,
 *   heartbeat, JSON, API, HTTP, BGP, IXC, GIS, NOC, JSON, payload, log
 *
 * Esses termos podem aparecer no meio de frases traduzidas:
 *   "Versão Firmware: 2.7.3"   ← Firmware preservado como nome próprio
 *   "Último Heartbeat: 12s"    ← Heartbeat idem
 *   "Senha Wi-Fi muda"          ← Wi-Fi idem
 */

import { ptBR } from "./ptBR.js";
import { enUS } from "./enUS.js";

const DICTS = {
  "pt-BR": ptBR,
  "en-US": enUS,
};

const DEFAULT_LOCALE = "pt-BR";
let currentLocale = DEFAULT_LOCALE;

export function setLocale(locale) {
  if (!DICTS[locale]) {
    console.warn(`[i18n] locale desconhecido: ${locale} — usando ${DEFAULT_LOCALE}`);
    currentLocale = DEFAULT_LOCALE;
    return;
  }
  currentLocale = locale;
}

export function getLocale() {
  return currentLocale;
}

export function availableLocales() {
  return Object.keys(DICTS);
}

/**
 * t(key, vars?) — Tradução por chave hierárquica.
 *
 * @param {string} key  Ex: 'common.apply' ou 'schedule.showingOf'
 * @param {object} vars Variáveis para interpolação {n: 5} → "...5..."
 * @returns {string}    Texto traduzido OU a key se não achar
 *
 * NOTA: SEMPRE retorna string — mesmo se key inexistente, devolve a key
 * inteira (facilita detectar visualmente no UI: "schedule.foo" aparece
 * como tal e gera report no check-i18n.js).
 */
export function t(key, vars) {
  if (!key || typeof key !== "string") return String(key ?? "");

  const dict = DICTS[currentLocale] || DICTS[DEFAULT_LOCALE];
  const parts = key.split(".");
  let val = dict;
  for (const p of parts) {
    if (val == null || typeof val !== "object") {
      // Fallback: tenta no DEFAULT_LOCALE se mudou para outro locale
      if (currentLocale !== DEFAULT_LOCALE) {
        return tInLocale(key, vars, DEFAULT_LOCALE);
      }
      return key;
    }
    val = val[p];
  }
  if (val == null) {
    if (currentLocale !== DEFAULT_LOCALE) {
      return tInLocale(key, vars, DEFAULT_LOCALE);
    }
    return key;
  }
  if (typeof val !== "string") return key;
  return interpolate(val, vars);
}

function tInLocale(key, vars, locale) {
  const dict = DICTS[locale];
  if (!dict) return key;
  const parts = key.split(".");
  let val = dict;
  for (const p of parts) {
    if (val == null || typeof val !== "object") return key;
    val = val[p];
  }
  if (typeof val !== "string") return key;
  return interpolate(val, vars);
}

function interpolate(template, vars) {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, k) =>
    vars[k] != null ? String(vars[k]) : `{${k}}`
  );
}

/**
 * Hook React simples — retorna { t, locale, setLocale } para uso em
 * componentes funcionais. Esta versão NÃO força re-render no setLocale
 * (não há estado React); para isso será preciso um Context provider
 * em sprint futura quando habilitarmos troca dinâmica de locale.
 *
 * Por enquanto: useT() é só um açúcar conveniente para ler `t`.
 */
export function useT() {
  return { t, locale: currentLocale, setLocale };
}

// Atalho default export para imports mais simples
export default { t, useT, setLocale, getLocale, availableLocales };
